import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';

// RxJS
import { Subject, switchMap, takeUntil } from 'rxjs';

// Services
import { RecordsService } from '@modules/records/services/records.service';
import { AlertService } from '@modules/elements/services/alert.service';

// Types
import { Clip } from '@modules/preview/types/clip';
import { Record, RecordTaskType } from '@modules/records/types/record';

@Component({
    selector: 'app-transcode-in-out-points',
    templateUrl: './transcode-in-out-points.component.html',
    styleUrl: './transcode-in-out-points.component.less',
    standalone: false
})
export class TranscodeInOutPointsComponent implements OnInit, OnChanges, OnDestroy {
  
  // Inputs
  @Input() clip?: Clip;
  @Input() record?: Record;
  @Input() title?: string;
  @Input() serverId?: string;

  // Public
  public points?: {
    start?: string,
    end?: string
  };
  public ffmpegPoints?: {
    start?: string,
    end?: string
  };
  public inPoint: string = '00:00:00:00';
  public outPoint: string = '00:00:00:00';
  public inProgress = false;

  // Private
  private alive = new Subject<void>();
  private timecode?: string;
  private ffmpegTimecode?: string;

  /**
   * Constructor
   */

  constructor(
    private recordsService: RecordsService,
    private alertService: AlertService,
  ) { }

  /**
   * Component lifecycle
   */

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('record' in changes || 'serverId' in changes) {
      this.points = {};
      if (this.record && this.serverId) {
        this.points = Record.getICapPoints(this.record, this.serverId);
        this.ffmpegPoints = Record.getFfmpegPoints(this.record, this.serverId);
      }
      this.inPoint = this.points?.start || (!this.clip?.drop ? '00:00:00;00' : '00:00:00:00');
      this.outPoint = this.points?.end || (!this.clip?.drop ? '00:00:00;00' : '00:00:00:00');
    }
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Methods
   */

  setTimecode(timecode: string | undefined): void {
    this.timecode = timecode;
  }

  setFFmpegTimecode(ffmpegTimecode: string | undefined): void {
    this.ffmpegTimecode = ffmpegTimecode;
  }

  setInPoint(): void {
    if (this.record && this.timecode && this.serverId) {
      if (!this.points) {
        this.points = {};
      }
      this.points.start = this.timecode;
      this.inPoint = this.timecode;
      Record.updateICapPoints(this.recordsService, this.record, this.serverId, this.points);
      if (this.ffmpegTimecode && this.ffmpegPoints) {
        this.ffmpegPoints.start = this.ffmpegTimecode;
        Record.updateFfmpegPoints(this.recordsService, this.record, this.serverId, this.ffmpegPoints);
      }
      this.recordsService.updateRecord(this.record);
    }
  }

  setOutPoint(): void {
    if (this.record && this.timecode && this.serverId) {
      if (!this.points) {
        this.points = {};
      }
      this.points.end = this.timecode;
      this.outPoint = this.timecode;
      Record.updateICapPoints(this.recordsService, this.record, this.serverId, this.points);
      if (this.ffmpegTimecode && this.ffmpegPoints) {
        this.ffmpegPoints.end = this.ffmpegTimecode;
        Record.updateFfmpegPoints(this.recordsService, this.record, this.serverId, this.ffmpegPoints);
      }
      this.recordsService.updateRecord(this.record);
    }
  }

  process(): void {
    if (this.record?.id && this.inPoint && this.outPoint && this.serverId) {
      this.alertService.show(
        'Process',
        'Would you like to process?',
        ['Yes', 'No'],
        0
      )
      .subscribe(response => {
        if (response === 0) {
          this.recordsService.startICap(this.record!.id!, [this.serverId!], this.inPoint,  this.outPoint)
            .pipe(
              switchMap(() => this.recordsService.startManualTask(this.record!.id!, [this.serverId!], 'icapStartManual')),
              takeUntil(this.alive)
            )
            .subscribe(() => this.inProgress = true);
        }
      });
    }
  }

}
